/**
 * Compliance > CoAs > detail. The certificate PDF on the left (embedded viewer
 * with the browser's native toolbar), the editable fields on the right.
 *
 * Editable: PO number(s), document type, supplier, item #, manufacturer.
 * Item description is not stored on the CoA; it's looked up live by item #
 * (GET /v1/compliance/coas/items/{itemNumber}), which searches every Sage item
 * and then the CounterPoint item master under the CoA grant. Read-only: email
 * subject, sender,
 * date received, file name (the email-derived facts). Saving sets the POs,
 * re-files the PDF in SharePoint to the matching PO folder, and locks the row
 * so the automated filer will not overwrite the manual edit.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Save } from 'lucide-react';
import { useSession } from '@/lib/useSession';
import { formatDateTime } from '@/lib/dateUtils';
import {
  capitalizeFirst,
  coaPdfUrl,
  docTypeLabel,
  hasComplianceCoaWrite,
  statusLabel,
  useCoaDetail,
  useEditCoa,
  useCoaItem,
  DOC_TYPE_LABELS,
} from '@/lib/useCoas';

export function CoaDetail() {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  const canWrite = hasComplianceCoaWrite(user?.permissions, user?.superAdmin);

  const detailQ = useCoaDetail(documentId);
  const edit = useEditCoa();

  const backHref = { pathname: '/compliance/coas', search: backSearch ? `?${backSearch}` : '' };

  return (
    <div className="pb-20">
      <Link
        to={backHref}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to CoAs
      </Link>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Compliance &middot; CoAs</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink break-all">
          {detailQ.data?.file_name ?? 'Certificate'}
        </h1>
      </div>

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : detailQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {detailQ.error.message}</p>
      ) : detailQ.data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PdfViewer documentId={documentId ?? ''} />
          <FieldsPanel
            detail={detailQ.data}
            canWrite={canWrite}
            saving={edit.isPending}
            onSave={(body) =>
              edit.mutate(
                { documentId: documentId ?? '', body },
                { onError: () => undefined },
              )
            }
            saveError={edit.error?.message ?? null}
            saved={edit.isSuccess}
          />
        </div>
      ) : null}
    </div>
  );
}

function PdfViewer({ documentId }: { documentId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(coaPdfUrl(documentId), { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`${res.status} ${text.slice(0, 200)}`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoked = objUrl;
        setUrl(objUrl);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [documentId]);

  return (
    <div className="border border-amp-line rounded-lg bg-amp-white overflow-hidden dark:border-amp-dark-line dark:bg-amp-dark-surface min-h-[600px] flex flex-col">
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : err ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Could not load the PDF ({err}). The certificate may not be in SharePoint yet.
        </div>
      ) : url ? (
        <iframe title="Certificate PDF" src={url} className="flex-1 w-full" style={{ minHeight: 600 }} />
      ) : null}
    </div>
  );
}

function FieldsPanel({
  detail,
  canWrite,
  saving,
  onSave,
  saveError,
  saved,
}: {
  detail: import('@/lib/useCoas').CoaDetail;
  canWrite: boolean;
  saving: boolean;
  onSave: (body: import('@/lib/useCoas').EditCoaBody) => void;
  saveError: string | null;
  saved: boolean;
}) {
  const initialPos = useMemo(() => detail.pos.map((p) => p.po_number).join(', '), [detail.pos]);
  const [docType, setDocType] = useState(detail.doc_type);
  const [supplier, setSupplier] = useState(detail.sender_name ?? '');
  const [posText, setPosText] = useState(initialPos);
  const [itemNumber, setItemNumber] = useState(detail.item_number ?? '');
  const [manufacturer, setManufacturer] = useState(detail.manufacturer ?? '');

  // Re-seed local fields when the loaded document changes.
  useEffect(() => {
    setDocType(detail.doc_type);
    setSupplier(detail.sender_name ?? '');
    setPosText(detail.pos.map((p) => p.po_number).join(', '));
    setItemNumber(detail.item_number ?? '');
    setManufacturer(detail.manufacturer ?? '');
  }, [detail.document_id, detail.doc_type, detail.sender_name, detail.pos, detail.item_number, detail.manufacturer]);

  // Debounce the item # before looking it up, so the description does not
  // refetch on every keystroke.
  const [lookupItemNumber, setLookupItemNumber] = useState(itemNumber.trim());
  useEffect(() => {
    const t = setTimeout(() => setLookupItemNumber(itemNumber.trim()), 400);
    return () => clearTimeout(t);
  }, [itemNumber]);
  const itemLookup = useCoaItem(lookupItemNumber || undefined);

  const submit = () => {
    const pos = posText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    onSave({ doc_type: docType, supplier: supplier.trim(), pos, item_number: itemNumber.trim(), manufacturer: manufacturer.trim() });
  };

  return (
    <div className="space-y-5">
      <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">Certificate details</h2>
        <dl className="space-y-2 text-sm">
          <ReadRow label="Status">
            {statusLabel(detail.status)}{detail.locked ? ' (manually edited)' : ''}
          </ReadRow>
          <ReadRow label="Email subject">{detail.subject ?? '--'}</ReadRow>
          <ReadRow label="Sender">{detail.sender_email ?? '--'}</ReadRow>
          <ReadRow label="Date received">{formatDateTime(detail.received_at)}</ReadRow>
          <ReadRow label="File name">{detail.file_name}</ReadRow>
          <ReadRow label="PO source">{capitalizeFirst(detail.po_source)}</ReadRow>
          <ReadRow label="Confidence">{capitalizeFirst(detail.confidence)}</ReadRow>
          {detail.last_edited_by && (
            <ReadRow label="Last edited">{detail.last_edited_by} &middot; {formatDateTime(detail.last_edited_at)}</ReadRow>
          )}
          {detail.sharepoint_web_url && (
            <ReadRow label="SharePoint">
              <a
                href={detail.sharepoint_web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-amp-olive hover:underline dark:text-amp-olive-dark"
              >
                Open file <ExternalLink className="h-3 w-3" />
              </a>
            </ReadRow>
          )}
        </dl>
      </div>

      <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
          {canWrite ? 'Review and correct' : 'Filed POs'}
        </h2>
        {canWrite ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
                PO number(s)
              </label>
              <input
                type="text"
                value={posText}
                onChange={(e) => setPosText(e.target.value)}
                placeholder="e.g. 527630, 527505"
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              />
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1">
                Separate multiple POs with commas. Saving re-files the PDF into the matching PO folder.
              </p>
            </div>
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Document type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              >
                {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Supplier</label>
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              />
            </div>
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Item #</label>
              <input
                type="text"
                value={itemNumber}
                onChange={(e) => setItemNumber(e.target.value)}
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              />
            </div>
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
                Item description
              </label>
              <div className="w-full min-h-[34px] flex items-center border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-cream-warm text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
                {!lookupItemNumber ? (
                  '--'
                ) : itemLookup.data ? (
                  (itemLookup.data.description ?? 'No description on file') +
                  (itemLookup.data.inactive ? ' (inactive)' : '')
                ) : itemLookup.error?.status === 404 ? (
                  'Item # not found in Sage or CounterPoint'
                ) : itemLookup.error ? (
                  'Could not look up the item right now'
                ) : (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
              </div>
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1">
                Auto-filled from Sage or CounterPoint by item #. Not editable here.
              </p>
            </div>
            <div>
              <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Manufacturer</label>
              <input
                type="text"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
              />
            </div>
            {saveError && <p className="text-xs text-red-700 dark:text-red-300">{saveError}</p>}
            {saved && !saveError && <p className="text-xs text-green-700 dark:text-green-300">Saved.</p>}
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          </div>
        ) : (
          <ul className="text-sm text-amp-ink dark:text-amp-dark-ink space-y-1">
            <li>Type: {docTypeLabel(detail.doc_type)}</li>
            {detail.pos.length ? (
              detail.pos.map((p) => <li key={p.po_id} className="font-mono text-xs">PO {p.po_number}</li>)
            ) : (
              <li className="text-amp-ink-muted dark:text-amp-dark-ink-muted">No PO assigned (unmatched).</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
